import { Box } from '@mui/material';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CheckboxGroupField from 'components/forms/fields/checkbox-group-field/checkbox-group-field.component';
import DaysMultipleSelectField from 'components/forms/fields/days-multiple-select-field/days-multiple-select-field.component';
import TimeRangePickerField from 'components/forms/fields/time-range-picker-field/time-range-picker-field.component';
import { useKitchenCapacityContext } from 'components/kitchen-capacity/kitchen-capacity.context';
import { filterOrderLimitsOverlappedEvents } from 'components/kitchen-capacity/order-limits-calendar/order-limits.utils';
import NumericStepInputField from 'components/forms/fields/numeric-step-input-field/numeric-step-input-field.component';
import { AnimatePresence } from 'framer-motion';
import { FormFieldPresence } from 'components/motion/motion.component';

type SetOrderLimitsFormProps = {
  eventId: string;
};
const SetOrderLimitsForm = ({ eventId }: SetOrderLimitsFormProps) => {
  const { t } = useTranslation();
  const orderLimitsType = useWatch({ name: 'orderLimitsType' });
  const daysOfWeek = useWatch({ name: 'daysOfWeek' });
  const timeRange = useWatch({ name: 'timeRange' });
  const kitchenThrottleValue = useWatch({ name: 'kitchenThrottleValue' });
  const kitchenThrottleVolume = useWatch({ name: 'kitchenThrottleVolume' });
  const {
    orderLimits: { orderLimitsEvents },
  } = useKitchenCapacityContext();

  const orderTypeOptions = [
    {
      label: t('forms.setOrderLimitsForm.fields.orderLimitsType.options.value'),
      id: 'VALUE',
    },
    {
      label: t(
        'forms.setOrderLimitsForm.fields.orderLimitsType.options.volume',
      ),
      id: 'VOLUME',
    },
  ];

  const hasOverlap =
    filterOrderLimitsOverlappedEvents(
      {
        eventId,
        orderLimitsType,
        daysOfWeek,
        timeRange,
        kitchenThrottleValue,
        kitchenThrottleVolume,
      },
      orderLimitsEvents,
    ).length > 0;

  return (
    <Box className="field-container" display="grid" gap={3}>
      <CheckboxGroupField
        name="orderLimitsType"
        options={orderTypeOptions}
        FormGroupProps={{
          sx: {
            flexDirection: 'row',
          },
        }}
      />

      <AnimatePresence>
        {orderLimitsType.includes('VALUE') && (
          <FormFieldPresence>
            <NumericStepInputField
              name="kitchenThrottleValue"
              id="kitchenThrottleValue"
              label={t(
                'forms.setOrderLimitsForm.fields.kitchenThrottle.value.label',
              )}
              step={1}
              allowNegative={false}
              prefix={`$ `}
              decimalScale={2}
              fixedDecimalScale
              decrementDisabled={val => {
                return val < 2;
              }}
              incrementDisabled={val => {
                return val > 9999;
              }}
              data-testid="SetOrderLimitsForm.KitchenThrottleValue.Field"
              style={{ margin: 0 }}
            />
          </FormFieldPresence>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {orderLimitsType.includes('VOLUME') && (
          <FormFieldPresence>
            <NumericStepInputField
              name="kitchenThrottleVolume"
              id="kitchenThrottleVolume"
              label={t(
                'forms.setOrderLimitsForm.fields.kitchenThrottle.volume.label',
              )}
              allowNegative={false}
              allowDecimal={false}
              step={1}
              decrementDisabled={val => {
                return val < 2;
              }}
              incrementDisabled={val => {
                return val > 9999;
              }}
              data-testid="SetOrderLimitsForm.KitchenThrottleVolume.Field"
              style={{ margin: 0 }}
            />
          </FormFieldPresence>
        )}
      </AnimatePresence>

      <DaysMultipleSelectField
        name="daysOfWeek"
        label={t('forms.setOrderLimitsForm.fields.daysOfWeek.label')}
        data-testid="SetOrderLimitsForm.DaysOfWeek.Field"
        TextFieldProps={{ margin: 'none' }}
      />

      <TimeRangePickerField
        name="timeRange"
        helperText={
          hasOverlap &&
          t('forms.setOrderLimitsForm.fields.timeRange.warnings.overlap')
        }
        helperTextSeverity={'warning'}
        minutesStep={15}
        fromLabel={t('forms.setOrderLimitsForm.fields.timeRange.fromLabel')}
        toLabel={t('forms.setOrderLimitsForm.fields.timeRange.toLabel')}
        data-testid="SetOrderLimitsForm.TimeRangePicker.Field"
      />
    </Box>
  );
};

export default SetOrderLimitsForm;
